import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";

import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "@src/../entrypoints/theme";
import { extractNostoProductIds, getNostoResponse } from "./nostoUtils";
import shopify from "../../../../js/shopify";
import { ProductTile } from "@project-stories/ecommerce/Product/ProductTile";

import "./MinicartRecommendations.scss";

interface MinicartRecommendationsProps {
  cartItems: any[];
  settings: any;
  globals: any;
  manualProducts?: any[];
  collectionProducts?: any[];
  className?: string;
}

const MinicartRecommendations = ({
  cartItems,
  settings,
  globals,
  manualProducts = [],
  collectionProducts = [],
  className,
}: MinicartRecommendationsProps) => {
  const browserWindow = window as any;
  const carouselSectionId = "reco-carousel-minicart";
  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const hasLoadedOnFirstOpenRef = useRef(false);

  const [products, setProducts] = useState<any[]>([]);
  const [recoData, setRecoData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const recommendationTitle = settings?.recommendation_title || "You May Also Like";
  const recommendationSource = settings?.recommendation_source || "nosto";
  const recommendationLimit = Math.max(Number(settings?.recommendation_limit || 3), 1);
  const nostoPlacementId = settings?.recommendation_nosto_placement_id;
  const skeletonCount = 4;

  const normalizeLiquidProduct = (product: any) => {
    if (!product) return null;

    if (product.objectType === "product" && Array.isArray(product.variants)) {
      return product;
    }

    const variants = Array.isArray(product.variants)
      ? product.variants.map((variant: any) => ({
          id: Number(variant.id),
          available: Boolean(variant.available),
          compare_at_price: variant.compare_at_price || null,
          options: [variant.option1, variant.option2, variant.option3].filter(Boolean),
          image: {
            alt: variant?.featured_image?.alt || product.title,
            media_type: "image",
            src: variant?.featured_image?.src || null,
          },
        }))
      : [];

    const options = Array.isArray(product.options)
      ? product.options.map((option: any) => {
          if (typeof option === "string") return option;
          return option?.name;
        })
      : [];

    const optionsWithValues = Array.isArray(product.options)
      ? product.options.map((option: any, index: number) => {
          if (typeof option === "string") {
            return {
              name: option,
              position: index + 1,
              values: variants.map((variant: any) => variant.options[index]).filter(Boolean),
            };
          }

          return {
            name: option.name,
            position: option.position || index + 1,
            values: option.values || [],
          };
        })
      : [];

    const media = Array.isArray(product.media)
      ? product.media
      : Array.isArray(product.images)
      ? product.images.map((image: any) => ({
          alt: image?.alt || product.title,
          media_type: "image",
          src: image?.src || image,
        }))
      : [];

    return {
      objectType: "product",
      id: Number(product.id),
      title: product.title,
      handle: product.handle,
      url: `/products/${product.handle}`,
      tags: Array.isArray(product.tags)
        ? product.tags
        : String(product.tags || "")
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean),
      price: Number(product.price || variants?.[0]?.price || 0),
      compare_at_price: Number(product.compare_at_price || variants?.[0]?.compare_at_price || 0) || null,
      variants,
      media,
      featuredImage: {
        alt: product?.featured_image?.alt || product.title,
        media_type: "image",
        src: product?.featured_image?.src || media?.[0]?.src || null,
      },
      options,
      options_with_values: optionsWithValues,
    };
  };

  const loadRecommendations = useCallback(async () => {
      setIsLoading(true);
      setRecoData(null);

      try {
        if (recommendationSource === "manual") {
          const normalized = (manualProducts || []).map(normalizeLiquidProduct).filter(Boolean).slice(0, recommendationLimit);
          setProducts(normalized);
          return;
        }

        if (recommendationSource === "collection") {
          const normalized = (collectionProducts || []).map(normalizeLiquidProduct).filter(Boolean).slice(0, recommendationLimit);
          setProducts(normalized);
          return;
        }

        if (!browserWindow.nostojs || !nostoPlacementId) {
          console.warn("Nosto.js is not available or placement ID is missing");
          setProducts([]);
          return;
        }

        const nostoResponse = await getNostoResponse({
          browserWindow,
          cartItems,
          globals,
          nostoPlacementId,
        });

        if (!nostoResponse) {
          console.warn("No response from Nosto API");
          setProducts([]);
          return;
        }
        console.log("Nosto API response:", nostoResponse);
        const placementScopedData =
          nostoResponse?.recommendations?.[nostoPlacementId] ||
          nostoResponse?.campaigns?.recommendations?.[nostoPlacementId] ||
          nostoResponse;

        const productIds = extractNostoProductIds(placementScopedData).slice(0, recommendationLimit);

        if (productIds.length === 0) {
          console.log("No recommended products found");
          setProducts([]);
          setRecoData(nostoResponse);
          return;
        }

        const fetchedProducts = await shopify.getShopifyProductList(productIds);
        setRecoData(nostoResponse);
        setProducts((fetchedProducts || []).slice(0, recommendationLimit));
      } catch (error) {
        console.error("Error loading recommendations:", error);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    }, [
      browserWindow,
      globals,
      manualProducts,
      collectionProducts,
      nostoPlacementId,
      recommendationLimit,
      recommendationSource,
  ]);

  useEffect(() => {
    const tryLoadOnFirstOpen = () => {
      if (hasLoadedOnFirstOpenRef.current) return;

      const isMinicartVisible = Boolean(browserWindow?.Alpine?.store?.("global")?.isMinicartVisible);
      if (!isMinicartVisible) return;

      hasLoadedOnFirstOpenRef.current = true;
      loadRecommendations();
    };

    tryLoadOnFirstOpen();

    const intervalId = window.setInterval(tryLoadOnFirstOpen, 150);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [browserWindow, loadRecommendations]);

  useEffect(() => {
    if (recommendationSource !== "nosto") return;
    if (!recoData || !widgetRef.current || !browserWindow.nostojs) return;

    browserWindow.nostojs((api: any) => {
      try {
        api?.attributeProductClicksInCampaign?.(widgetRef.current, recoData);
      } catch {
        return;
      }
    });
  }, [browserWindow, recommendationSource, recoData]);

  const navigateCarousel = useCallback((direction: "prev" | "next") => {
    const action = direction === "prev" ? "previous" : "next";
    window.dispatchEvent(
      new CustomEvent("carousel-navigate", {
        detail: {
          section_id: carouselSectionId,
          action,
        },
      }),
    );
  }, []);

  if (!isMobile) {

    if (isLoading === false && products.length === 0) {
      return null;
    }
   
    return (
      <aside ref={widgetRef} className={clsx("minicart-recom w-[248px] max-h-[calc(100vh)] p-6 bg-white box overflow-y-auto", className)}
      >
        <h5 className="text-center text-[#54565A]">{recommendationTitle}</h5>
        <div className="flex flex-col gap-6 mt-6">
          {isLoading
            ? Array.from({ length: skeletonCount }).map((_, index) => (
                <div key={`minicart-reco-desktop-skeleton-${index}`} className="animate-pulse">
                  <div className="aspect-[3/4] w-full rounded bg-[#ECEEEF]" />
                  <div className="mt-4 h-4 w-3/4 rounded bg-[#ECEEEF]" />
                  <div className="mt-2 h-4 w-1/2 rounded bg-[#ECEEEF]" />
                  <div className="mt-4 h-10 w-full rounded bg-[#ECEEEF]" />
                </div>
              ))
            : products.map((product) => (
                <ProductTile
                  key={`minicart-reco-desktop-${product.id}`}
                  product={normalizeLiquidProduct(product)}
                  swatches_position="bottom"
                  show_quick_add={true}
                  show_title_color={true}
                />
              ))}
        </div>
      </aside>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <section ref={widgetRef} className={clsx("minicart-recom mt-3 p-4 border-t border-[#ECEBEB] bg-[#F5F5F5]", className)}>
      <div className="flex items-center justify-between mb-5 px-3">
        <h3 className="bc uppercase text-[#000]">{recommendationTitle}</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="w-11 h-11 rounded-full bg-[#ECEEEF] p-0 min-h-[44px]"
            onClick={() => navigateCarousel("prev")}
          >
            <i className="icon icon-arrow-left text-[24px] text-[#00263E]" />
          </button>
          <button
            type="button"
            className="w-11 h-11 rounded-full bg-[#ECEEEF] p-0 min-h-[44px]"
            onClick={() => navigateCarousel("next")}
          >
            <i className="icon icon-arrow-right text-[24px] text-[#00263E]" />
          </button>
        </div>
      </div>

      <div
        className="relative flex gap-4 -mr-4 pb-2"
        x-data="carousel"
        x-init={
          `$nextTick(() => {
            const initCarousel = () => {
              initialise(false, false, false, false, false, '${carouselSectionId}', true, false)
            }

            if ($store.global.isMinicartVisible) {
              initCarousel()
            }

            $watch('$store.global.isMinicartVisible', (isVisible) => {
              if (isVisible) {
                initCarousel()
              }
            })
          })`
        }
      >
        <div x-ref="carousel" className="w-full h-full pl-1">
          {products.map((product) => (
            <div key={`minicart-reco-mobile-${product.id}`} className="shrink-0 w-[216px] min-h-[450px] px-1">
              <ProductTile product={normalizeLiquidProduct(product)} swatches_position="bottom" show_quick_add={true} show_title_color={true} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MinicartRecommendations;